import { ChevronRight, type LucideIcon } from 'lucide-react';
import React from 'react';

import { Link } from '@/components/design-system';
import {
  COLOR_SCALE,
  FONT_WEIGHTS,
  PADDING_Y,
  RADIUS_SCALE,
  MARGIN_Y,
  PADDING_X,
  MARGIN_X,
  SPACE_X_SCALE,
  SIZE_SCALE,
  MARGIN_LEFT,
  MARGIN_RIGHT,
  FLEX,
  FLEX_ALIGN,
  FLEX_JUSTIFY,
  TYPE_SCALE,
  INTERACTIVE_STATES,
  FLEX_SHRINK,
  LINE_HEIGHT,
  BORDER_WIDTH,
} from '@/components/design-system/tokens';

const MENU_LINK_BASE = `${FLEX.flex} ${FLEX_ALIGN.center} ${SPACE_X_SCALE['3']} ${PADDING_X['3']} ${PADDING_Y['2.5']} ${MARGIN_X['1']} ${TYPE_SCALE.xs} ${COLOR_SCALE.foreground} hover:${COLOR_SCALE['surface-subtle']} hover:${COLOR_SCALE.foreground} transition-colors ${RADIUS_SCALE.md} focus:outline-none ${INTERACTIVE_STATES.focus.ring} focus:ring-gray-400 focus:ring-inset focus:${COLOR_SCALE['surface-subtle']}`;

const MENU_LINK_NESTED = `${FLEX.flex} ${FLEX_ALIGN.center} ${SPACE_X_SCALE['2']} ${PADDING_X['3']} ${PADDING_Y['2']} ${TYPE_SCALE.xs} ${COLOR_SCALE['foreground-muted']} hover:${COLOR_SCALE['surface-subtle']} hover:${COLOR_SCALE.foreground} transition-colors ${RADIUS_SCALE.md} focus:outline-none ${INTERACTIVE_STATES.focus.ring} focus:ring-gray-400 focus:ring-inset`;

interface MenuLinkProps {
  to: string;
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  badge?: React.ReactNode;
  variant?: 'primary' | 'nested';
  role?: string;
  title?: string;
}

export const MenuLink = ({
  to,
  icon: Icon,
  label,
  onClick,
  badge,
  variant = 'primary',
  role = 'menuitem',
  title,
}: MenuLinkProps) => {
  const isNested = variant === 'nested';
  return (
    <Link
      to={to}
      className={isNested ? MENU_LINK_NESTED : MENU_LINK_BASE}
      onClick={onClick}
      role={role}
      title={title}
    >
      <Icon
        className={`${isNested ? `${SIZE_SCALE['3.5']} ${SIZE_SCALE['3.5']}` : `${SIZE_SCALE['4']} ${SIZE_SCALE['4']}`} ${COLOR_SCALE['admin-muted-text']} ${FLEX_SHRINK['0']}`}
      />
      <span
        className={`${isNested ? FONT_WEIGHTS.light : FONT_WEIGHTS.regular} min-w-0 flex-1 break-words ${LINE_HEIGHT.snug} overflow-hidden`}
      >
        {label}
      </span>
      {badge && <span className={`${FLEX_SHRINK['0']}`}>{badge}</span>}
    </Link>
  );
};

export const MenuDivider = () => (
  <div className={`${BORDER_WIDTH.thin} ${COLOR_SCALE['admin-border']} ${MARGIN_Y['2']}`} />
);

interface CollapsibleSectionProps {
  icon: LucideIcon;
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

export const CollapsibleSection = ({
  icon,
  label,
  isExpanded,
  onToggle,
  children,
}: CollapsibleSectionProps) => (
  <>
    <SectionToggle icon={icon} label={label} isExpanded={isExpanded} onToggle={onToggle} />
    {isExpanded && <SectionPanel>{children}</SectionPanel>}
  </>
);

interface SectionToggleProps {
  icon: LucideIcon;
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
}

const SectionToggle = ({ icon: Icon, label, isExpanded, onToggle }: SectionToggleProps) => (
  <button
    onClick={onToggle}
    className={`${FLEX.flex} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.between} ${SIZE_SCALE.full} ${PADDING_X['3']} ${PADDING_Y['2.5']} ${MARGIN_X['1']} ${TYPE_SCALE.xs} ${COLOR_SCALE.foreground} hover:${COLOR_SCALE['surface-subtle']} hover:${COLOR_SCALE.foreground} transition-colors ${RADIUS_SCALE.md} focus:outline-none ${INTERACTIVE_STATES.focus.ring} focus:ring-gray-400 focus:ring-inset`}
    role="menuitem"
    aria-expanded={isExpanded}
  >
    <div
      className={`${FLEX.flex} ${FLEX_ALIGN.center} ${SPACE_X_SCALE['3']} min-w-0 flex-1 overflow-hidden`}
    >
      <Icon
        className={`${SIZE_SCALE['4']} ${SIZE_SCALE['4']} ${COLOR_SCALE['foreground-muted']} ${FLEX_SHRINK['0']}`}
      />
      <span className={`${FONT_WEIGHTS.light} break-words ${LINE_HEIGHT.snug} overflow-hidden`}>
        {label}
      </span>
    </div>
    <ChevronRight
      className={`${SIZE_SCALE['3']} ${SIZE_SCALE['3']} ${COLOR_SCALE['icon-muted']} transition-transform duration-200 ${FLEX_SHRINK['0']} ${MARGIN_LEFT['2']} ${
        isExpanded ? 'rotate-90' : ''
      }`}
    />
  </button>
);

interface SectionPanelProps {
  children: React.ReactNode;
}

const SectionPanel = ({ children }: SectionPanelProps) => (
  <div
    className={`${COLOR_SCALE['surface-muted']}/50 ${BORDER_WIDTH['2']} ${COLOR_SCALE['admin-border']} ${MARGIN_LEFT['3']} ${MARGIN_RIGHT['1']} ${RADIUS_SCALE.md} ${MARGIN_Y['1']}`}
  >
    {children}
  </div>
);
